// Web Push subscribe/unsubscribe for the PWA. The server side is
// cid-agent-server notification.service.js (register/unregister + VAPID
// public-key endpoint); the SW handlers live in public/push-sw.js.
import * as notifsApi from './api/notifications';
import { viewAs } from './api/client';

// VAPID public key (base64url) → the Uint8Array pushManager.subscribe expects.
const b64ToUint8 = (b64) => {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export async function subscribeToPush() {
  // HQ viewing as the agent: never point the agent's notifications at HQ's browser.
  if (viewAs.active) throw new Error('Notifications are off while viewing as an agent.');
  if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) {
    throw new Error('Push is not supported on this device. On iPhone, add the app to your home screen first.');
  }
  if (Notification.permission === 'denied') {
    throw new Error('Notifications are blocked for this app — allow them in your browser settings first.');
  }
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') throw new Error('Notification permission was not granted');

  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) throw new Error('The app is still installing — try again in a moment.');

  const r = await notifsApi.getPushPublicKey();
  const key = (r?.data ?? r)?.key;
  if (!key) throw new Error('Push is not configured on the server yet');

  const sub =
    (await reg.pushManager.getSubscription()) ||
    (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToUint8(key) }));
  await notifsApi.createPushRegister({ subscription: sub.toJSON(), platform: 'web', ua: navigator.userAgent });
  return sub;
}

// Best-effort (logout): revoke server-side, then drop the browser subscription.
export async function unsubscribeFromPush() {
  if (viewAs.active) return; // this browser's subscription is not the agent's
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    const sub = await reg?.pushManager?.getSubscription();
    if (!sub) return;
    await notifsApi.deletePushUnregister({ data: { endpoint: sub.endpoint } }).catch(() => {});
    await sub.unsubscribe();
  } catch (_) { /* never block logout on push cleanup */ }
}
